import { useState } from 'react'
import { SOURCE_LABEL, formatPerComparableUnit } from '@domain/product'
import type { Merchant, PricePoint } from '@domain/product'
import { formatBRL } from '@shared/money/format'
import { civilDateOf, timeInHousehold } from '@shared/date/timezone'
import { dayAndMonth } from '@shared/date/civil'
import { fullDayLabel } from '@ui/finance/day-groups'
import { useFinanceRepositories } from '@ui/finance/finance-context'

/**
 * Uma linha do histórico de preço, que abre com todos os detalhes.
 *
 * ## Fechada por padrão, e a linha continua respondendo o principal
 *
 * Quando, quanto, e por unidade. É o que uma lista de preços precisa mostrar
 * quinze vezes seguidas sem virar parede de texto. O resto — quantas
 * embalagens, onde, quem, quando foi digitado — é o que se olha quando um
 * número **surpreende**, e é aí que ele aparece.
 *
 * ## `<details>`, e não estado em React
 *
 * Abrir e fechar é comportamento nativo do navegador, com teclado e leitor de
 * tela já resolvidos. Um `useState` por linha daria o mesmo resultado visual e
 * teria que refazer a acessibilidade à mão.
 *
 * ## As duas datas são coisas diferentes, e por isso as duas aparecem
 *
 * `observedAt` é o dia em que o preço **era aquele**; `createdAt` é quando
 * alguém digitou. Registrar hoje um preço visto na semana passada é normal, e
 * mostrar só uma das duas esconderia justamente o caso em que elas divergem.
 *
 * ## Anular fica DENTRO do detalhe, e em dois toques
 *
 * Anular muda as estatísticas do produto, e um clique só, no resumo da linha,
 * seria acionado sem querer numa lista de quinze. Dentro do `<details>` já
 * exige abrir; o segundo toque confirma. Não é diálogo — diálogo é para o que
 * não dá para desfazer, e o registro continua no banco.
 *
 * Sem `aoAnular`, a linha é só leitura: é assim que a lista de anulados a usa,
 * onde o botão não faria sentido nenhum.
 */
export function PontoDePreco({
  ponto,
  lojas,
  vencido = false,
  aoAnular,
}: {
  ponto: PricePoint
  lojas: Merchant[]
  /**
   * A mesma loja já foi conferida depois, e por um preço diferente.
   *
   * **Riscado, e não escondido.** O número foi verdade no dia — o que ele
   * perdeu é o direito de responder "onde compensa", que é pergunta sobre
   * hoje. Sumir com ele apagaria a evidência de que o preço subiu, que é
   * justamente o que o histórico existe para guardar. Ver `vencidos`.
   */
  vencido?: boolean
  /** Ausente deixa a linha só de leitura. */
  aoAnular?: () => void
}) {
  const loja = lojas.find((l) => l.id === ponto.merchantId) ?? null

  return (
    <li className="border-b border-[var(--border)] text-sm">
      <details>
        <summary className="flex cursor-pointer list-none items-baseline gap-2 py-1">
          <span className="text-[var(--text-muted)]">{dayAndMonth(ponto.observedAt)}</span>
          <span className="text-xs text-[var(--text-muted)]">{SOURCE_LABEL[ponto.source]}</span>
          {/* O rótulo em texto, e não só o risco: risco é cor e forma, e quem
              usa leitor de tela não recebe nenhum dos dois. */}
          {vencido && (
            <span className="text-xs text-[var(--text-faint)]">preço antigo desta loja</span>
          )}
          <span className="flex-1" />
          {/* Só os NÚMEROS riscam. Riscar a linha inteira levaria junto a data e
              a origem, que continuam valendo — o que deixou de valer é o preço. */}
          <span className={`tabular-nums ${vencido ? 'text-[var(--text-faint)] line-through' : ''}`}>
            {formatBRL(ponto.totalCents)}
          </span>
          <span
            className={`tabular-nums ${vencido ? 'text-[var(--text-faint)] line-through' : 'text-[var(--text-muted)]'}`}
          >
            {formatPerComparableUnit(ponto.pricePerBaseUnitMilliCents, ponto.baseUnit)}
          </span>
        </summary>

        <dl className="grid grid-cols-2 gap-x-3 gap-y-1 py-2 text-xs">
          {/* Quantidade e preço unitário só aparecem quando há mais de uma
              embalagem: "1 × R$ 4,29 = R$ 4,29" é ruído. */}
          {ponto.quantity !== 1 && (
            <Fato rotulo="Quantidade">
              {`${String(ponto.quantity)} × ${formatBRL(ponto.priceCents)}`}
            </Fato>
          )}
          <Fato rotulo="Total pago">{formatBRL(ponto.totalCents)}</Fato>
          <Fato rotulo="Por unidade">
            {formatPerComparableUnit(ponto.pricePerBaseUnitMilliCents, ponto.baseUnit)}
          </Fato>

          <Fato rotulo="Onde">
            {loja === null ? (
              // Loja apagada cai no id, e ponto sem loja diz que não tem: os
              // dois são estados diferentes e a tela não os junta.
              ponto.merchantId === null ? (
                'não registrado'
              ) : (
                ponto.merchantId
              )
            ) : ponto.sourceUrl === null ? (
              loja.name
            ) : (
              <a
                href={ponto.sourceUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="alvo justify-start underline"
              >
                {loja.name}
              </a>
            )}
          </Fato>

          <Fato rotulo="Visto em">{fullDayLabel(ponto.observedAt)}</Fato>
          <Fato rotulo="Registrado em">
            {ponto.createdAt === '' ? '—' : diaEHora(ponto.createdAt)}
          </Fato>

          <Fato rotulo="Por quem">
            {`${ponto.recordedBy.label}${ponto.recordedBy.kind === 'ai' ? ' 🤖' : ''}`}
          </Fato>
          <Fato rotulo="Origem">{SOURCE_LABEL[ponto.source]}</Fato>

          {ponto.note !== null && (
            <div className="col-span-2">
              <dt className="text-[var(--text-muted)]">Anotação</dt>
              <dd>{ponto.note}</dd>
            </div>
          )}

          {ponto.receiptItemId !== null && (
            <div className="col-span-2 text-[var(--text-muted)]">
              {/* Sem link: o ponto guarda o id do ITEM da nota, e não o da nota
                  — não dá para montar a rota a partir dele. Dizer que veio de
                  uma nota já explica por que este preço não foi digitado. */}
              Veio de uma nota fiscal.
            </div>
          )}

          {ponto.supersedesId !== null && (
            <div className="col-span-2 text-[var(--text-muted)]">
              {/* O corrigido continua no banco e fora de toda conta — nunca se
                  reescreve o passado, mas o errado também não se soma. */}
              Corrige um registro anterior, que saiu das contas.
            </div>
          )}
          {ponto.voidedAt !== null && (
            <div className="col-span-2 text-[var(--text-muted)]">
              Anulado por {ponto.voidedBy?.label ?? 'alguém'} em{' '}
              {diaEHora(ponto.voidedAt)}.
            </div>
          )}
        </dl>

        {aoAnular === undefined ? null : <Anular ponto={ponto} aoAnular={aoAnular} />}
      </details>
    </li>
  )
}

/**
 * O botão de anular, em dois toques.
 *
 * A falha é dita e a linha **fica**: sumir com ela depois de uma gravação que
 * não aconteceu mostraria como resolvido o que não foi.
 */
function Anular({ ponto, aoAnular }: { ponto: PricePoint; aoAnular: () => void }) {
  const { productRepository } = useFinanceRepositories()
  const [confirmando, setConfirmando] = useState(false)
  const [anulando, setAnulando] = useState(false)
  const [falhou, setFalhou] = useState(false)

  async function anule() {
    setAnulando(true)
    setFalhou(false)
    try {
      await productRepository.voidPricePoint(ponto.id)
      aoAnular()
    } catch (erro: unknown) {
      console.error('PontoDePreco: não deu para anular', erro)
      setFalhou(true)
      setAnulando(false)
      setConfirmando(false)
    }
  }

  if (!confirmando) {
    return (
      <div className="pb-2">
        <button
          type="button"
          onClick={() => {
            setConfirmando(true)
          }}
          className="rounded-lg px-2 py-1 text-xs text-[var(--text-muted)] underline"
        >
          Anular este preço
        </button>
        {falhou ? (
          <p role="alert" className="text-xs text-[var(--text-muted)]">
            Não deu para anular. Tente de novo.
          </p>
        ) : null}
      </div>
    )
  }

  return (
    <div className="flex flex-wrap items-center gap-2 pb-2 text-xs">
      <span className="text-[var(--text-muted)]">
        Sai das contas e do histórico. O registro continua guardado.
      </span>
      <button
        type="button"
        disabled={anulando}
        onClick={() => {
          void anule()
        }}
        className="rounded-lg border border-[var(--border)] px-2 py-1 text-[var(--negative)] disabled:opacity-60"
      >
        {anulando ? 'Anulando…' : 'Anular mesmo'}
      </button>
      <button
        type="button"
        disabled={anulando}
        onClick={() => {
          setConfirmando(false)
        }}
        className="rounded-lg px-2 py-1 text-[var(--text-muted)]"
      >
        Cancelar
      </button>
    </div>
  )
}

function Fato({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-[var(--text-muted)]">{rotulo}</dt>
      <dd className="tabular-nums">{children}</dd>
    </div>
  )
}


/**
 * `'2026-08-12T13:04:00Z'` → `'12/08 às 10:04'`, **no fuso da casa**.
 *
 * Pelo `civilDateOf`/`timeInHousehold`, e não por `toISOString().slice()`: um
 * registro feito às 22h de São Paulo é 01h do dia seguinte em UTC, e o corte
 * mostraria a data e a hora erradas justamente nos registros de fim de noite.
 * É o mesmo erro que o lembrete da Fase 9 já pagou uma vez.
 */
function diaEHora(iso: string): string {
  const instante = new Date(iso)
  if (Number.isNaN(instante.getTime())) return '—'
  const dia = civilDateOf(instante)
  return `${dayAndMonth(dia)} às ${timeInHousehold(instante)}`
}
